import { isEmpty } from 'lodash'
import { toast as sonnerToast } from 'sonner'

// Imports nothing from the store on purpose: socket middleware fires toasts, and
// the store imports that middleware, so a store import here would be circular.

export const PERMISSION_DENIED_MESSAGE =
  "You don't have permission to do that. Ask an admin if you need access."

// handleResponse's text for a 403 whose body carries no message.
export const DEFAULT_FORBIDDEN_MESSAGE = 'User not authorized to perform that action.'

// One id for every generic permission toast, so a page whose requests are all
// refused shows the message once instead of once per request.
export const PERMISSION_DENIED_TOAST_ID = 'permission-denied'

/**
 * Whether a fetchingAPI rejection was the backend refusing the user (403).
 *
 * @param {*} error The value a fetchingAPI call rejected with
 * @returns {boolean}
 */
export const isPermissionDenied = (error) => Boolean(error?.isPermissionDenied)

// Some 403s explain themselves ("User not authorized for unredacted Balto", a
// feature that isn't enabled) and an admin can't grant those, so "ask an admin"
// would send people to the wrong fix. A capability name ("Missing permission:
// users:write"), balto-auth's bare "Forbidden", or handleResponse's placeholder
// says nothing a user can act on, so those get the generic text.
const GENERIC_DENIAL = /^(missing permission|forbidden\b)/i

export const hasOwnDenialReason = (error) => {
  const reason = error?.message
  return Boolean(reason) && reason !== DEFAULT_FORBIDDEN_MESSAGE && !GENERIC_DENIAL.test(reason)
}

const deniedMessage = (error) =>
  hasOwnDenialReason(error) ? error.message : PERMISSION_DENIED_MESSAGE

// sonner's own signature when nothing is left to pass, so a call without options
// still reaches sonner (and tests that assert on it) as `error(message)`.
const sonnerError = (message, options) =>
  isEmpty(options) ? sonnerToast.error(message) : sonnerToast.error(message, options)

// `cause` is the caught error, named as in `new Error(message, { cause })`.
const error = (message, { cause, ...options } = {}) => {
  if (!isPermissionDenied(cause)) {
    return sonnerError(message, options)
  }
  if (hasOwnDenialReason(cause)) {
    return sonnerError(cause.message, options)
  }

  return sonnerToast.error(PERMISSION_DENIED_MESSAGE, {
    id: PERMISSION_DENIED_TOAST_ID,
    ...options,
  })
}

// A 403 rejection shows its denial message whatever the caller's `error` says.
const promise = (promiseOrFn, { error: onError, ...options } = {}) =>
  sonnerToast.promise(promiseOrFn, {
    ...options,
    ...(onError !== undefined && {
      error: (cause) => {
        if (isPermissionDenied(cause)) {
          return deniedMessage(cause)
        }

        return typeof onError === 'function' ? onError(cause) : onError
      },
    }),
  })

/**
 * Balto's `toast`: sonner's API, with failed requests made permission-aware.
 * Import it in place of `toast` from 'sonner'.
 *
 * - `toast.error(message, { cause })`: pass the caught error. On a 403 the user
 *   sees the backend's own denial reason if it wrote one, else "You don't have
 *   permission…" (once per burst); any other failure shows `message`. A caller's
 *   own `id` (replacing a loading toast) still wins.
 * - `toast.promise(promise, { error })`: a 403 rejection shows the same denial
 *   message instead of `error`.
 * - Everything else (`toast()`, `success`, `info`, `warning`, `loading`,
 *   `message`, `custom`, `dismiss`, `getHistory`, `getToasts`) is sonner's own.
 *
 * Each method calls sonner when it runs, not when this module loads, so a test's
 * `vi.spyOn(toast, …)` on sonner's `toast` still sees the call.
 *
 * @example
 * try {
 *   await createUser(user)
 *   toast.success('User created.')
 * } catch (error) {
 *   toast.error('Failed to create user.', { cause: error })
 * }
 * @import import { toast } from '@/components/toasts/toast.helpers'
 */
export const toast = Object.assign((...args) => sonnerToast(...args), {
  success: (...args) => sonnerToast.success(...args),
  info: (...args) => sonnerToast.info(...args),
  warning: (...args) => sonnerToast.warning(...args),
  error,
  loading: (...args) => sonnerToast.loading(...args),
  message: (...args) => sonnerToast.message(...args),
  custom: (...args) => sonnerToast.custom(...args),
  promise,
  dismiss: (...args) => sonnerToast.dismiss(...args),
  getHistory: (...args) => sonnerToast.getHistory(...args),
  getToasts: (...args) => sonnerToast.getToasts(...args),
})
